import { useState, useEffect, useCallback, useRef } from "react";

const initialResource = {
	loading: false,
	error: false,
	data: null,
	headers: null,
};

const useResource = (options, autoLoad = true, debug = false) => {
	const [shouldLoad, setShouldLoad] = useState(autoLoad);
	const [resource, setResource] = useState(initialResource);
	const reset = useCallback(() => {
		setResource(initialResource);
		setShouldLoad(false);
	}, []);

	const log = useCallback(
		(...msg) => {
			if (debug) console.log(...msg);
		},
		[debug]
	);

	const mounted = useRef(true);

	useEffect(() => {
		mounted.current = true;
		return () => (mounted.current = false);
	}, []);

	const startLoad = useCallback(() => setShouldLoad(true), []);

	const load = useCallback(async () => {
		log("[uR]: Load called ");
		if (resource.loading || !shouldLoad || !options?.url) {
			log(
				"[uR]: Load stopped ",
				resource.loading,
				!shouldLoad,
				!options?.url
			);
			return;
		}
		log("[uR]: Load started ");
		setShouldLoad(false);
		let resp;
		setResource({ loading: true, error: false, data: null, headers: null });

		try {
			let reqOptions = {
				headers: {
					"Content-Type": "application/json",
				},
				method: "GET",
				...options,
			};
			if (
				(options?.method === "POST" || options?.method === "PUT") &&
				options.data
			) {
				reqOptions = {
					...reqOptions,
					body: JSON.stringify(options.data),
				};
			}
			resp = await fetch(options.url, reqOptions);

			if (resp.status !== 200 && resp.status !== 201) {
				throw new Error("RESP_ERR");
			}
			try {
				resp = await resp.json();
			} catch (error) {
				throw new Error("JSON_ERR");
			}
		} catch ({ message: errMessage }) {
			log(`[uR]: Load error! ${errMessage}`);
			if (mounted.current) {
				return setResource({
					loading: false,
					error:
						errMessage === "RESP_ERR" || errMessage === "JSON_ERR"
							? "An unknown error occured"
							: "Could not connect to the server",
					data: null,
				});
			} else {
				return log("[uR]: Load error but NOT MOUNTED ");
			}
		}

		if (mounted.current) {
			log("[uR]: Load complete:", resp);
			setResource({
				loading: false,
				error: false,
				data: resp,
				headers: false,
			});
		} else {
			log("[uR]: Load complete but NOT MOUNTED ");
		}
	}, [options, resource.loading, shouldLoad, log]);

	useEffect(() => {
		if (!resource.loading && shouldLoad) {
			load();
		}
	}, [resource.loading, shouldLoad, load]);

	return [resource, startLoad, reset];
};

export default useResource;
